import { createSlice } from '@reduxjs/toolkit';
import { tvInitialState } from '../../models';

const tvSlice = createSlice({
  name: 'tv',
  initialState: tvInitialState,
  reducers: {
    // TV show list actions
    setTVShows: (state, action) => {
      state.tvShows = action.payload;
    },
    
    addTVShows: (state, action) => {
      const newTVShows = action.payload;
      const existingIds = state.tvShows.map(tv => tv.id);
      const uniqueTVShows = newTVShows.filter(tv => !existingIds.includes(tv.id));
      state.tvShows = [...state.tvShows, ...uniqueTVShows];
    },
    
    setTotalResults: (state, action) => {
      state.totalResults = action.payload;
    },
    
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    
    // Loading states
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setLoadingMore: (state, action) => {
      state.isLoadingMore = action.payload;
    },
    
    // Error handling
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    resetFilters: (state) => {
      state.filters = {
        selectedGenre: 'all',
        sortBy: 'trending',
        imdbRating: 0,
        rottenTomatoesRating: 0,
        fromYear: null,
        toYear: null,
      };
    },
    
    // View mode
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    
    // Prefetch management
    addToPrefetchQueue: (state, action) => {
      const tvIds = action.payload;
      const newIds = tvIds.filter(id => 
        !state.prefetchedIds.includes(id) && 
        !state.prefetchQueue.includes(id)
      );
      state.prefetchQueue.push(...newIds);
    },
    
    setPrefetching: (state, action) => {
      state.isPrefetching = action.payload;
    },
    
    markAsPrefetched: (state, action) => {
      const tvIds = action.payload;
      tvIds.forEach(id => {
        if (!state.prefetchedIds.includes(id)) {
          state.prefetchedIds.push(id);
        }
      });
      state.prefetchQueue = state.prefetchQueue.filter(id => !tvIds.includes(id));
    },
    
    clearPrefetchQueue: (state) => {
      state.prefetchQueue = [];
    },
    
    // Enhanced data cache
    setEnhancedData: (state, action) => {
      const { tvId, data } = action.payload;
      state.enhancedDataCache[tvId] = data;
    },
    
    bulkSetEnhancedData: (state, action) => {
      state.enhancedDataCache = { ...state.enhancedDataCache, ...action.payload };
    },
    
    clearEnhancedDataCache: (state) => {
      state.enhancedDataCache = {};
    },
    
    // Scroll restoration is now handled by TanStack Query
    
    // Performance tracking
    updatePerformanceMetrics: (state, action) => {
      const { type, value } = action.payload;
      
      if (type === 'fetchTime') {
        state.lastFetchTime = value;
      } else if (type === 'fetchCount') {
        state.fetchCount += 1;
      }
    },
    
    // Reset state
    resetTVState: (state) => {
      state.tvShows = [];
      state.totalResults = 0;
      state.currentPage = 1;
      state.hasMore = true;
      state.error = null;
      state.isLoading = false;
      state.isLoadingMore = false;
      state.prefetchQueue = [];
      state.prefetchedIds = [];
      state.isPrefetching = false;
    },
    
    // Client-side sorting and filtering
    sortTVShows: (state, action) => {
      const sortBy = action.payload;
      
      state.tvShows.sort((a, b) => {
        switch (sortBy) {
          case 'vote_average':
            const aImdb = a.enhanced_ratings?.real_ratings?.imdb?.rating || 0;
            const bImdb = b.enhanced_ratings?.real_ratings?.imdb?.rating || 0;
            const aRt = a.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating || 0;
            const bRt = b.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating || 0;
            const aTmdb = a.vote_average || 0;
            const bTmdb = b.vote_average || 0;
            
            // Check if TV shows have only TMDB ratings (no IMDB or RT)
            const aHasOnlyTmdb = !aImdb && !aRt && aTmdb;
            const bHasOnlyTmdb = !bImdb && !bRt && bTmdb;
            
            // If one has only TMDB and the other has real ratings, prioritize real ratings
            if (aHasOnlyTmdb && !bHasOnlyTmdb) return 1; // a goes to bottom
            if (!aHasOnlyTmdb && bHasOnlyTmdb) return -1; // b goes to bottom
            
            // If both have only TMDB, sort by TMDB rating
            if (aHasOnlyTmdb && bHasOnlyTmdb) {
              return bTmdb - aTmdb;
            }
            
            // For TV shows with real ratings, use IMDB if available, otherwise use RT converted to 10-point scale
            const aRating = aImdb || (aRt / 10) || aTmdb;
            const bRating = bImdb || (bRt / 10) || bTmdb;
            return bRating - aRating;
          

          
          case 'popularity':
          default:
            return (b.popularity || 0) - (a.popularity || 0);
        }
      });
    },
    
    filterTVShows: (state, action) => {
      const { imdbRating, rottenTomatoesRating } = action.payload;
      
      state.tvShows = state.tvShows.filter(tvShow => {
        const hasImdb = tvShow.enhanced_ratings?.real_ratings?.imdb?.rating;
        const hasRt = tvShow.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating;
        const hasOnlyTmdb = !hasImdb && !hasRt && tvShow.vote_average;
        
        // Include TV shows with only TMDB ratings (they will be sorted to bottom)
        if (hasOnlyTmdb) {
          return true;
        }
        
        // IMDB rating filter
        if (imdbRating > 0) {
          const tvImdbRating = tvShow.enhanced_ratings?.real_ratings?.imdb?.rating;
          if (tvShow.enhanced_ratings && tvImdbRating !== null && tvImdbRating !== undefined && tvImdbRating < imdbRating) {
            return false;
          }
        }
        
        // Rotten Tomatoes filter
        if (rottenTomatoesRating > 0) {
          const tvRtScore = tvShow.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating;
          if (tvShow.enhanced_ratings && tvRtScore !== null && tvRtScore !== undefined && tvRtScore < rottenTomatoesRating) {
            return false;
          }
        }
        
        return true;
      });
    },
  },
});

export const {
  setTVShows,
  addTVShows,
  setTotalResults,
  setCurrentPage,
  setHasMore,
  setLoading,
  setLoadingMore,
  setError,
  clearError,
  setFilters,
  resetFilters,
  setViewMode,
  addToPrefetchQueue,
  setPrefetching,
  markAsPrefetched,
  clearPrefetchQueue,
  setEnhancedData,
  bulkSetEnhancedData,
  clearEnhancedDataCache,
  setScrollPosition,
  setContentState,
  updatePerformanceMetrics,
  resetTVState,
  sortTVShows,
  filterTVShows,
} = tvSlice.actions;

export default tvSlice.reducer; 